'use strict';

var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var Dropdown = require('../../Dropdown/src/Dropdown.js');
require('../../Space/src/Space.js');
require('../../Textarea/src/Textarea.js');
require('../../Typography/src/Heading.js');
require('../../Typography/src/Link.js');
require('../../Typography/src/ListItem.js');
require('../../Typography/src/OrderedList.js');
require('../../Typography/src/Text.js');
require('../../Typography/src/UnorderedList.js');
var useOptionsMap = require('../hooks/useOptionsMap.js');
var Autocomplete_module = require('../styles/Autocomplete.module.less.js');
var EmptyContent = require('./EmptyContent.js');
var InputView = require('./InputView.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const MIN_ROWS = 1;
// TODO мобильный вид требует доработки
const mobile = false;
/**
 * @description The Autocomplete component allow user to type and select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Autocomplete placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
// eslint-disable-next-line max-statements
const Autocomplete = React__namespace.forwardRef((props, ref) => {
    const { defaultValue, value, options, autoFocus, invalid, disabled, className, menuClassName, mode, icon, readOnly, placement, fixedWidth, resizable, emptyContent, title, closeOnSelect = true, flip = true, enableCustomValue, optionsSort = true, onChange, onSelect, onSearch, onClear, onFocus, onBlur, ...restProps } = props;
    const cx = reactUtils.useStyles(Autocomplete_module.default);
    const isFirstRender = React__namespace.useRef(true);
    const containerRef = React__namespace.useRef(null);
    const inputRef = React__namespace.useRef(null);
    const menuRef = React__namespace.useRef(null);
    const hasUserInput = React__namespace.useRef(false);
    const formControlContext = reactUtils.useFormControlContext();
    const [visible, setVisible] = reactUtils.useBoolean(false);
    const [focused, setFocused] = reactUtils.useBoolean(false);
    const [fixWidth, setFixWidth] = reactUtils.useBoolean(fixedWidth);
    const [innerValue, setInnerValue] = reactUtils.useStateCallback(value ?? defaultValue);
    const [inputValue, setInputValue] = React__namespace.useState('');
    const [innerOptions, setInnerOptions] = React__namespace.useState(options || []);
    const [selected, setSelected] = React__namespace.useState(false);
    const getOptionByValue = useOptionsMap.useOptionsMap(options);
    const isMultiple = mode === 'multiple';
    const displayValue = React__namespace.useMemo(() => {
        if (isMultiple) {
            if (visible && !selected)
                return inputValue;
            if (!Array.isArray(innerValue) || !innerValue.length)
                return '';
            return innerValue
                .map((v) => getOptionByValue(v)?.label)
                .filter(Boolean)
                .join(', ');
        }
        if (visible) {
            return inputValue;
        }
        const { label } = getOptionByValue(innerValue);
        const newLabel = enableCustomValue && label !== inputValue ? inputValue : label;
        return newLabel;
    }, [isMultiple, enableCustomValue, selected, visible, inputValue, innerValue, getOptionByValue]);
    const setValue = React__namespace.useCallback((newValue) => {
        setInnerValue(newValue, (val) => {
            let label = '';
            if (!isMultiple) {
                label = getOptionByValue(val).label;
                setInputValue(label || val);
            }
            else {
                label = Array.isArray(val) ? val.map((v) => getOptionByValue(v)?.label).join(', ') : '';
            }
            onChange?.(label ?? '');
            onSelect?.(val ?? '');
        });
    }, [isMultiple, setInnerValue, getOptionByValue, onSelect, onChange]);
    const closeMenuOnSelect = React__namespace.useCallback((value) => (reactUtils.isDefined(closeOnSelect) && reactUtils.isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect), [closeOnSelect]);
    const handleSelect = React__namespace.useCallback((value) => {
        setValue(value);
        isMultiple && setSelected(true);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [isMultiple, closeMenuOnSelect, setValue, setVisible]);
    const handleClick = React__namespace.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleChangeInput = React__namespace.useCallback((event) => {
        const { value } = event.currentTarget;
        setInputValue(value);
        if (onSearch === undefined) {
            setInnerOptions(options.filter((o) => o.label.toLowerCase().indexOf(value.toLowerCase()) >= 0));
        }
        hasUserInput.current = true;
        onChange?.(value);
        onSearch?.(value);
        !visible && inputRef.current?.click();
    }, [options, visible, onChange, onSearch]);
    const handleFocusInput = React__namespace.useCallback((event) => {
        if (!focused) {
            const { label } = getOptionByValue(innerValue);
            setFocused.on();
            if (!enableCustomValue) {
                setInputValue(label);
            }
            onFocus?.(event);
        }
    }, [focused, enableCustomValue, innerValue, getOptionByValue, onFocus, setFocused]);
    const handleBlurInput = React__namespace.useCallback((event) => {
        if (menuRef.current?.contains(event.relatedTarget)) {
            return;
        }
        const { label } = getOptionByValue(innerValue);
        if (enableCustomValue && label !== inputValue) {
            setInnerValue(undefined);
        }
        onBlur?.(event);
        setVisible.off();
        setFocused.off();
    }, [enableCustomValue, inputValue, innerValue, onBlur, getOptionByValue, setFocused, setInnerValue, setVisible]);
    const handleClickClear = React__namespace.useCallback((event) => {
        event.stopPropagation();
        setValue('');
        onClear?.();
        inputRef.current?.focus();
    }, [setValue, onClear]);
    const handleEmptyContentClick = React__namespace.useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const handleClose = React__namespace.useCallback(() => {
        setVisible.off();
    }, [setVisible]);
    const iconElement = React__namespace.useMemo(() => {
        if (visible && reactUtils.isDefined(innerValue) && innerValue !== '') {
            return React__namespace.createElement(reactIcons.Cross, { className: cx('icon'), onClick: handleClickClear });
        }
        if (icon || icon === null) {
            return icon;
        }
        return (React__namespace.createElement(reactIcons.ArrowDown, { className: cx('icon', {
                [`is-arrow-turned`]: visible,
            }) }));
    }, [cx, visible, innerValue, icon, handleClickClear]);
    const header = React__namespace.useMemo(() => {
        {
            return null;
        }
    }, [title, displayValue, iconElement, resizable, handleChangeInput]);
    reactUtils.useKeyPress({
        // закрывает выпадающее меню по Enter в случае с произвольным вводом
        enabled: !!enableCustomValue && (visible || focused) && !isMultiple && !innerOptions.length,
        keyMap: {
            Enter: (event) => {
                event.preventDefault();
                closeMenuOnSelect(innerValue) && inputRef.current?.click();
            },
        },
    }, [innerValue, innerOptions.length, visible, focused, enableCustomValue, isMultiple, closeMenuOnSelect]);
    // defaultValue needs only on first render
    React__namespace.useEffect(() => {
        let label = '';
        if (isFirstRender.current) {
            label = getOptionByValue(value ?? defaultValue).label;
            isFirstRender.current = false;
            setInnerValue(value ?? defaultValue);
        }
        else {
            label = getOptionByValue(value).label;
            setInnerValue(value);
        }
        if (enableCustomValue) {
            setInputValue((prevVal) => label ?? value ?? prevVal);
        }
        else {
            setInputValue((prevVal) => label ?? prevVal);
        }
    }, [value, defaultValue, enableCustomValue, getOptionByValue, setInnerValue, setInputValue]);
    React__namespace.useEffect(() => {
        autoFocus && inputRef.current?.focus();
    }, [autoFocus]);
    React__namespace.useEffect(() => {
        if (!visible) {
            hasUserInput.current = false;
            setSelected(false);
        }
    }, [visible]);
    React__namespace.useEffect(() => {
        setInnerOptions(options || []);
    }, [options]);
    React__namespace.useEffect(() => {
        if (hasUserInput.current)
            setFixWidth.on();
    }, [setFixWidth]);
    return (React__namespace.createElement(Dropdown.Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, 
        // eslint-disable-next-line @typescript-eslint/naming-convention
        className: cx(menuClassName, { 'hide-menu': !hasUserInput.current && !innerOptions.length }), mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixWidth, mobile: mobile, header: header, closeOnSelect: closeOnSelect, flip: flip, optionsSort: optionsSort, emptyContent: hasUserInput.current ? React__namespace.createElement(EmptyContent.EmptyContent, { emptyContent: emptyContent, onClick: handleEmptyContentClick }) : null, tabIndex: -1, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect, onClick: handleClick },
        React__namespace.createElement("div", { ref: containerRef, "data-qa": "Autocomplete", className: cx(className, 'container'), tabIndex: -1 },
            React__namespace.createElement(InputView.InputView, { ...restProps, ref: reactUtils.useMergedRef(ref, inputRef), 
                // eslint-disable-next-line @typescript-eslint/naming-convention
                className: cx('textarea', { 'with-icon': iconElement && !resizable, focus: focused || visible }), readOnly: readOnly || mobile || selected || (!visible && isMultiple), invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, resizable: resizable, rows: MIN_ROWS, wrap: resizable ? 'soft' : 'off', onChange: handleChangeInput, onFocus: handleFocusInput, onBlur: handleBlurInput }))));
});
Autocomplete.defaultProps = {
    fixedWidth: true,
    placement: 'bottom-start',
    resizable: false,
    closeOnSelect: true,
    optionsSort: true,
};
Autocomplete.displayName = 'Autocomplete';

exports.Autocomplete = Autocomplete;
//# sourceMappingURL=Autocomplete.js.map
